import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Users,
  CreditCard,
  DollarSign,
  Activity,
  Wallet,
  PiggyBank,
  Clock,
  TicketIcon,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  Search,
  Sparkles,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrgSettings } from "@/lib/org-settings-context";
import { SpendingChart } from "@/components/customer/spending-chart";

interface DashboardKPIs {
  totalCustomers: number;
  activeCards: number;
  totalTransactions: number;
  totalVolumeCents: number;
  pendingKyc: number;
  openTickets: number;
  amountFundedCents: number;
  remainingBalanceCents: number;
}

interface AnalyticsTimeSeries {
  dailyVolume: { date: string; volume: number; count: number }[];
  dailyWallet: { date: string; credits: number; debits: number }[];
  dailyFunding: { date: string; amount: number; count: number }[];
  txnByType: { category: string; amount: number }[];
  txnByStatus: { category: string; amount: number }[];
  fundBySource: { category: string; amount: number }[];
  recentCardTransactions: CardTransaction[];
  recentFundLoads: FundLoad[];
}

interface CardTransaction {
  id: string;
  cardId: string;
  userId: string;
  type: string;
  status: string;
  amountCents: number;
  currency: string;
  merchantName: string | null;
  mcc: string | null;
  createdAt: string;
}

interface FundLoad {
  id: string;
  walletId: string;
  userId: string;
  amountCents: number;
  currency: string;
  sourceType: string;
  sourceReference: string | null;
  status: string;
  feeCents: number;
  createdAt: string;
  completedAt: string | null;
}

type DateRange = "7d" | "30d" | "90d" | "all";

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "all", label: "All Time" },
];

const PIE_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];

function formatCompactNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
}

function filterByDateRange<T extends { date: string }>(data: T[], range: DateRange): T[] {
  if (range === "all") return data;
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
  const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return data.filter((d) => d.date >= cutoff);
}

function getSourceLabel(source: string) {
  switch (source) {
    case "bank_transfer": return "Bank Transfer";
    case "manual": return "Manual";
    case "card": return "Card";
    case "upi": return "UPI";
    default: return source.charAt(0).toUpperCase() + source.slice(1);
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case "COMPLETED": case "SETTLED": return <Badge variant="success">{status}</Badge>;
    case "PENDING": case "AUTHORIZED": return <Badge variant="secondary">{status}</Badge>;
    case "FAILED": case "DECLINED": case "REVERSED": return <Badge variant="destructive">{status}</Badge>;
    default: return <Badge variant="outline">{status}</Badge>;
  }
}

// Maps dashboard section IDs to their primary target element ID on this page
// and which tab to activate (if applicable)
const SECTION_TARGETS: Record<string, { scrollTo: string; tab?: string }> = {
  "total-volume": { scrollTo: "chart-transaction-volume" },
  "active-cards": { scrollTo: "kpi-active-cards" },
  "transactions": { scrollTo: "data-tables", tab: "transactions" },
  "customers": { scrollTo: "kpi-customers" },
  "pending-kyc": { scrollTo: "kpi-pending-kyc" },
  "open-tickets": { scrollTo: "kpi-open-tickets" },
  "amount-funded": { scrollTo: "data-tables", tab: "funding" },
  "remaining-balance": { scrollTo: "chart-wallet-flow" },
};

export default function OrgAnalytics() {
  const { user } = useAuth();
  const { formatCurrency } = useOrgSettings();
  const searchString = useSearch();
  const sectionParam = new URLSearchParams(searchString).get("section");
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [txnSearch, setTxnSearch] = useState("");
  const [fundSearch, setFundSearch] = useState("");
  const [txnSortOrder, setTxnSortOrder] = useState<"asc" | "desc">("desc");
  const [fundSortOrder, setFundSortOrder] = useState<"asc" | "desc">("desc");
  const [activeTab, setActiveTab] = useState("transactions");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const hasScrolled = useRef(false);

  const { data: kpis, isLoading: kpisLoading } = useQuery<DashboardKPIs>({
    queryKey: ["dashboard-kpis"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/dashboard/summary");
      if (!response.ok) throw new Error("Failed to fetch KPIs");
      return response.json();
    },
  });

  const { data: analytics, isLoading: analyticsLoading, refetch } = useQuery<AnalyticsTimeSeries>({
    queryKey: ["org-analytics-timeseries"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/analytics/time-series");
      if (!response.ok) throw new Error("Failed to fetch analytics");
      return response.json();
    },
  });

  // Filtered chart data
  const volumeData = useMemo(
    () => filterByDateRange(analytics?.dailyVolume ?? [], dateRange).map((d) => ({
      date: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      value: d.volume / 100,
      count: d.count,
    })),
    [analytics?.dailyVolume, dateRange]
  );

  const walletData = useMemo(
    () => filterByDateRange(analytics?.dailyWallet ?? [], dateRange).map((d) => ({
      date: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      credits: d.credits / 100,
      debits: d.debits / 100,
    })),
    [analytics?.dailyWallet, dateRange]
  );

  const fundingData = useMemo(
    () => filterByDateRange(analytics?.dailyFunding ?? [], dateRange).map((d) => ({
      date: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      value: d.amount / 100,
      count: d.count,
    })),
    [analytics?.dailyFunding, dateRange]
  );

  const txnByType = useMemo(
    () => (analytics?.txnByType ?? []).map((d, i) => ({ ...d, color: PIE_COLORS[i % PIE_COLORS.length] })),
    [analytics?.txnByType]
  );

  const txnByStatus = useMemo(
    () => (analytics?.txnByStatus ?? []).map((d, i) => ({ ...d, color: PIE_COLORS[i % PIE_COLORS.length] })),
    [analytics?.txnByStatus]
  );

  const fundBySource = useMemo(
    () => (analytics?.fundBySource ?? []).map((d, i) => ({
      category: getSourceLabel(d.category),
      amount: d.amount / 100,
      color: PIE_COLORS[i % PIE_COLORS.length],
    })),
    [analytics?.fundBySource]
  );

  // Filtered tables
  const filteredTxns = useMemo(() => {
    let txns = analytics?.recentCardTransactions ?? [];
    if (txnSearch) {
      const q = txnSearch.toLowerCase();
      txns = txns.filter(
        (t) =>
          t.merchantName?.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          t.type.toLowerCase().includes(q)
      );
    }
    return [...txns].sort((a, b) => {
      const da = new Date(a.createdAt).getTime();
      const db = new Date(b.createdAt).getTime();
      return txnSortOrder === "desc" ? db - da : da - db;
    });
  }, [analytics?.recentCardTransactions, txnSearch, txnSortOrder]);

  const filteredFunds = useMemo(() => {
    let loads = analytics?.recentFundLoads ?? [];
    if (fundSearch) {
      const q = fundSearch.toLowerCase();
      loads = loads.filter(
        (l) =>
          l.sourceReference?.toLowerCase().includes(q) ||
          l.id.toLowerCase().includes(q) ||
          l.sourceType.toLowerCase().includes(q)
      );
    }
    return [...loads].sort((a, b) => {
      const da = new Date(a.createdAt).getTime();
      const db = new Date(b.createdAt).getTime();
      return fundSortOrder === "desc" ? db - da : da - db;
    });
  }, [analytics?.recentFundLoads, fundSearch, fundSortOrder]);

  const isLoading = kpisLoading || analyticsLoading;

  // Scroll to and highlight the target section when arriving from dashboard
  useEffect(() => {
    if (!sectionParam || hasScrolled.current || isLoading) return;
    const target = SECTION_TARGETS[sectionParam];
    if (!target) return;

    // Set the tab if needed
    if (target.tab) setActiveTab(target.tab);

    // Small delay to let tab content render
    const timer = setTimeout(() => {
      const el = document.getElementById(target.scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(target.scrollTo);
        hasScrolled.current = true;
        // Remove highlight after animation
        setTimeout(() => setHighlightId(null), 2000);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [sectionParam, isLoading]);

  const statCards = [
    { id: "kpi-total-volume", title: "Total Volume", value: formatCurrency(kpis?.totalVolumeCents ?? 0), icon: DollarSign, color: "text-amber-500", bgColor: "bg-amber-500/10", isPrimary: true },
    { id: "kpi-active-cards", title: "Active Cards", value: formatCompactNumber(kpis?.activeCards ?? 0), icon: CreditCard, color: "text-primary", bgColor: "bg-primary/10" },
    { id: "kpi-transactions", title: "Transactions", value: formatCompactNumber(kpis?.totalTransactions ?? 0), icon: Activity, color: "text-primary", bgColor: "bg-primary/10" },
    { id: "kpi-customers", title: "Customers", value: formatCompactNumber(kpis?.totalCustomers ?? 0), icon: Users, color: "text-primary", bgColor: "bg-primary/10" },
    { id: "kpi-amount-funded", title: "Amount Funded", value: formatCurrency(kpis?.amountFundedCents ?? 0), icon: Wallet, color: "text-emerald-600 dark:text-emerald-400", bgColor: "bg-emerald-500/10" },
    { id: "kpi-remaining-balance", title: "Remaining Balance", value: formatCurrency(kpis?.remainingBalanceCents ?? 0), icon: PiggyBank, color: "text-teal-600 dark:text-teal-400", bgColor: "bg-teal-500/10" },
    { id: "kpi-pending-kyc", title: "Pending KYC", value: kpis?.pendingKyc ?? 0, icon: Clock, color: "text-warning-foreground", bgColor: "bg-warning/10" },
    { id: "kpi-open-tickets", title: "Open Tickets", value: kpis?.openTickets ?? 0, icon: TicketIcon, color: "text-destructive", bgColor: "bg-destructive/10" },
  ];

  return (
    <div className="p-6 space-y-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-reveal-up">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-display font-bold tracking-tight">Organization Analytics</h1>
            <Badge variant="default">
              <BarChart3 className="h-3 w-3 mr-1" />
              Live
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Comprehensive metrics, real-time charts, and transaction data
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRange)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()} title="Refresh data">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 animate-reveal-up" style={{ animationDelay: "0.1s" }}>
        {statCards.map((card) => (
          <Card
            key={card.title}
            id={card.id}
            className={cn(
              "transition-all duration-200 hover:shadow-md",
              card.isPrimary && "border-amber-500/30 shadow-glow-gold",
              highlightId === card.id && "ring-2 ring-primary shadow-lg animate-pulse"
            )}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", card.bgColor)}>
                  <card.icon className={cn("h-4 w-4", card.color)} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground truncate">{card.title}</p>
                  {isLoading ? (
                    <Skeleton className="h-6 w-16 mt-0.5" />
                  ) : (
                    <p className={cn("text-lg font-display font-bold", card.isPrimary && "text-amber-500")}>
                      {card.value}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row 1: Volume + Wallet Flow */}
      <div className="grid gap-6 lg:grid-cols-2 animate-reveal-up" style={{ animationDelay: "0.2s" }}>
        <div id="chart-transaction-volume" className={cn("rounded-xl transition-all duration-500", highlightId === "chart-transaction-volume" && "ring-2 ring-primary shadow-lg")}>
          <SpendingChart
            title="Transaction Volume"
            description="Daily card transaction volume"
            data={volumeData}
            type="area"
            height={280}
          />
        </div>
        <div id="chart-wallet-flow" className={cn("rounded-xl transition-all duration-500", highlightId === "chart-wallet-flow" && "ring-2 ring-primary shadow-lg")}>
          <SpendingChart
            title="Wallet Flow"
            description="Daily wallet credits vs debits"
            data={walletData}
            type="bar"
            dataKeys={{ x: "date", y: ["credits", "debits"] }}
            colors={["#10b981", "#ef4444"]}
            showLegend
            height={280}
          />
        </div>
      </div>

      {/* Charts Row 2: Funding + Breakdowns */}
      <div className="grid gap-6 lg:grid-cols-3 animate-reveal-up" style={{ animationDelay: "0.3s" }}>
        <div id="chart-fund-loads" className={cn("rounded-xl transition-all duration-500", highlightId === "chart-fund-loads" && "ring-2 ring-primary shadow-lg")}>
          <SpendingChart
            title="Fund Loads"
            description="Bulk funding over time"
            data={fundingData}
            type="bar"
            colors={["#10b981"]}
            height={240}
          />
        </div>
        <SpendingChart
          title="Transaction Types"
          description="Breakdown by type"
          data={txnByType}
          type="pie"
          height={240}
          showLegend
        />
        <SpendingChart
          title="Funding Sources"
          description="By payment method"
          data={fundBySource}
          type="pie"
          height={240}
          showLegend
        />
      </div>

      {/* Data Tables */}
      <Tabs value={activeTab} onValueChange={setActiveTab} id="data-tables" className={cn("animate-reveal-up transition-all duration-500", highlightId === "data-tables" && "ring-2 ring-primary rounded-xl shadow-lg")} style={{ animationDelay: "0.4s" }}>
        <TabsList>
          <TabsTrigger value="transactions" className="gap-1.5">
            <Activity className="h-3.5 w-3.5" />
            Card Transactions
          </TabsTrigger>
          <TabsTrigger value="funding" className="gap-1.5">
            <Wallet className="h-3.5 w-3.5" />
            Funding History
          </TabsTrigger>
        </TabsList>

        {/* Card Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4 mt-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search merchant, ID, or type..."
                value={txnSearch}
                onChange={(e) => setTxnSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {filteredTxns.length} transaction{filteredTxns.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Card>
            <CardContent className="p-0">
              {analyticsLoading ? (
                <div className="p-6 space-y-3">
                  {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : filteredTxns.length > 0 ? (
                <div className="overflow-auto max-h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer select-none sticky top-0 bg-card"
                          onClick={() => setTxnSortOrder((o) => o === "desc" ? "asc" : "desc")}
                        >
                          <span className="inline-flex items-center gap-1">Date <ArrowUpDown className="h-3 w-3" /></span>
                        </TableHead>
                        <TableHead className="sticky top-0 bg-card">Amount</TableHead>
                        <TableHead className="sticky top-0 bg-card">Type</TableHead>
                        <TableHead className="sticky top-0 bg-card">Merchant</TableHead>
                        <TableHead className="sticky top-0 bg-card">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTxns.map((txn) => (
                        <TableRow key={txn.id}>
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {new Date(txn.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                            <span className="block text-xs text-muted-foreground">
                              {new Date(txn.createdAt).toLocaleTimeString()}
                            </span>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(txn.amountCents)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{txn.type}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                            {txn.merchantName || "—"}
                          </TableCell>
                          <TableCell>{getStatusBadge(txn.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Activity className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground font-medium">No card transactions found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Funding History Tab */}
        <TabsContent value="funding" className="space-y-4 mt-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search reference, ID, or source..."
                value={fundSearch}
                onChange={(e) => setFundSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {filteredFunds.length} record{filteredFunds.length !== 1 ? "s" : ""}
              <span className="ml-2 font-medium text-emerald-600">
                Total: {formatCurrency(
                  filteredFunds.filter((l) => l.status === "COMPLETED").reduce((sum, l) => sum + l.amountCents, 0)
                )}
              </span>
            </p>
          </div>
          <Card>
            <CardContent className="p-0">
              {analyticsLoading ? (
                <div className="p-6 space-y-3">
                  {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : filteredFunds.length > 0 ? (
                <div className="overflow-auto max-h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer select-none sticky top-0 bg-card"
                          onClick={() => setFundSortOrder((o) => o === "desc" ? "asc" : "desc")}
                        >
                          <span className="inline-flex items-center gap-1">Date <ArrowUpDown className="h-3 w-3" /></span>
                        </TableHead>
                        <TableHead className="sticky top-0 bg-card">Amount</TableHead>
                        <TableHead className="sticky top-0 bg-card">Source</TableHead>
                        <TableHead className="sticky top-0 bg-card">Reference</TableHead>
                        <TableHead className="sticky top-0 bg-card">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredFunds.map((load) => (
                        <TableRow key={load.id}>
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {new Date(load.createdAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                            <span className="block text-xs text-muted-foreground">
                              {new Date(load.createdAt).toLocaleTimeString()}
                            </span>
                          </TableCell>
                          <TableCell
                            className={cn(
                              "font-semibold",
                              load.status === "COMPLETED"
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-muted-foreground"
                            )}
                          >
                            {formatCurrency(load.amountCents)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {getSourceLabel(load.sourceType)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                            {load.sourceReference || "—"}
                          </TableCell>
                          <TableCell>{getStatusBadge(load.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Wallet className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground font-medium">No funding records found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border/50 text-sm text-muted-foreground animate-reveal-up">
        <p>Last updated: <span className="font-mono">{new Date().toLocaleTimeString()}</span></p>
        <p className="text-xs">Data scoped to your organization</p>
      </div>
    </div>
  );
}
